import React, { useState } from "react";
import { usePreviewTargets, type PreviewTarget } from "@agentic_system/api/agent";
import { Spinner } from "@ui";
import { useDebouncedValue } from "@ui/hooks";
import { Search } from "lucide-react";

export interface SelectedTarget {
  id: number;
  name: string;
  type: "user" | "profile";
  profileClass?: string;
}

const LS_KEY = "agentic:preview:target";

export function loadTarget(): SelectedTarget | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveTarget(target: SelectedTarget) {
  localStorage.setItem(LS_KEY, JSON.stringify(target));
}

const SEARCH_TYPES = [
  { value: "Profile::Candidat", label: "Candidat" },
  { value: "Profile::Enseigne", label: "Enseigne" },
  { value: "Profile::Immo", label: "Bailleur" },
  { value: "Profile::Partner", label: "Partenaire" },
  { value: "Profile::ShoppingCenter", label: "Centre co." },
  { value: "Profile::Union", label: "Union" },
  { value: "Profile::Ville", label: "Ville" },
  { value: "Location", label: "Emplacement" },
  { value: "PointOfSale", label: "PDV" },
];

interface PreviewTargetSelectorProps {
  onSelect: (target: SelectedTarget) => void;
  onCancel?: () => void;
}

export function PreviewTargetSelector({ onSelect, onCancel }: PreviewTargetSelectorProps) {
  const [searchType, setSearchType] = useState("Profile::Candidat");
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 250);

  const { data: targets, isLoading, isFetching } = usePreviewTargets(searchType, debouncedQuery || undefined);

  const handleSelect = (t: PreviewTarget) => {
    const target: SelectedTarget = {
      id: t.id,
      name: t.name,
      type: "profile",
      profileClass: searchType,
    };
    saveTarget(target);
    onSelect(target);
  };

  return (
    <div className="absolute inset-0 z-20 bg-white/95 backdrop-blur-sm flex items-center justify-center">
      <div className="w-full max-w-lg px-6">
        <h2 className="text-lg font-semibold text-slate-800 text-center mb-4">
          Cible de test
        </h2>

        <div className="flex gap-2 mb-3">
          <select
            value={searchType}
            onChange={(e) => { setSearchType(e.target.value); setQuery(""); }}
            className="flex-shrink-0 px-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-default-500 focus:border-default-500"
          >
            {SEARCH_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>

          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher..."
              autoFocus
              className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-default-500 focus:border-default-500"
            />
            {isFetching && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Spinner />
              </div>
            )}
          </div>
        </div>

        <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : !targets || targets.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-400">
              {query ? "Aucun resultat." : "Tapez pour rechercher."}
            </div>
          ) : (
            targets.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => handleSelect(t)}
                className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-colors flex items-center gap-3"
              >
                <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-medium text-slate-500">
                    {t.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-700 truncate">{t.name}</div>
                  <div className="text-xs text-slate-400">
                    {(t as any).profile_type && <span>{(t as any).profile_type} · </span>}
                    ID {t.id}
                    {(t as any).completion_score > 0 && <span> · {(t as any).completion_score}%</span>}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="mt-3 w-full text-sm text-slate-400 hover:text-slate-600 transition-colors"
          >
            Annuler
          </button>
        )}
      </div>
    </div>
  );
}
